export type ProjectCMSWidget =
  | DynamicTextWidget
  | DynamicImageWidget
  | ClockWidget
  | ClockDigitalWidget
  | CurrentDateWidget
  | BrandLogoWidget
  | AppNameWidget
  | AppDescriptionWidget
  | AppLogoWidget
  | AppGridWidget
  | ChannelBannerContainerWidget
  | ChannelBannerTextWidget
  | ChannelTVWidget
  | ChannelLogoWidget
  | ChannelNumberWidget
  | ChannelNameWidget
  | ChannelGridWidget
  | LanguageWidget
  | MarriottAudioWidget
  | MarriottSubtitleWidget
  | ProgramTitleWidget
  | ProgramDescriptionWidget
  | ProgramTimeslotWidget
  | ProgramNextWidget
  | IPGWidget
  | WeatherWidget
  | MarriottWakeUpAlarmWidget
  | MarriottAlarmRingingWidget
  | MarriottPmsMessagesWidget
  | MarriottPmsMessagesIndicatorWidget
  | MarriottViewBillWidget
  | VODGridWidget
  | VODPlayerWidget
  | VODSummaryWidget
  | VODTitleWidget

export enum ProjectCMSWidgetType {
  CLOCK = 'CLOCK',
  CLOCK_DIGITAL = 'CLOCK_DIGITAL',
  CURRENT_DATE = 'CURRENT_DATE',
  BRAND_LOGO = 'BRAND_LOGO',
  APP_NAME = 'APP_NAME',
  APP_DESCRIPTION = 'APP_DESCRIPTION',
  APP_LOGO = 'APP_LOGO',
  APP_GRID = 'APP_GRID',
  CHANNEL_BANNER_CONTAINER = 'CHANNEL_BANNER_CONTAINER',
  CHANNEL_BANNER_TEXT = 'CHANNEL_BANNER_TEXT',
  CHANNEL_TV = 'CHANNEL_TV',
  CHANNEL_LOGO = 'CHANNEL_LOGO',
  CHANNEL_NUMBER = 'CHANNEL_NUMBER',
  CHANNEL_NAME = 'CHANNEL_NAME',
  CHANNEL_GRID = 'CHANNEL_GRID',
  LANGUAGE = 'LANGUAGE',
  MARRIOTT_AUDIO = 'MARRIOTT_AUDIO',
  MARRIOTT_SUBTITLE = 'MARRIOTT_SUBTITLE',
  PROGRAM_TITLE = 'PROGRAM_TITLE',
  PROGRAM_DESCRIPTION = 'PROGRAM_DESCRIPTION',
  PROGRAM_TIMESLOT = 'PROGRAM_TIMESLOT',
  PROGRAM_NEXT = 'PROGRAM_NEXT',
  IPG = 'IPG',
  WEATHER = 'WEATHER',
  MARRIOTT_WAKE_UP_ALARM = 'MARRIOTT_WAKE_UP_ALARM',
  MARRIOTT_ALARM_RINGING = 'MARRIOTT_ALARM_RINGING',
  MARRIOTT_PMS_MESSAGES = 'MARRIOTT_PMS_MESSAGES',
  MARRIOTT_PMS_MESSAGES_INDICATOR = 'MARRIOTT_PMS_MESSAGES_INDICATOR',
  MARRIOTT_VIEW_BILL = 'MARRIOTT_VIEW_BILL',
  VOD_GRID = 'VOD_GRID',
  VOD_PLAYER = 'VOD_PLAYER',
  VOD_SUMMARY = 'VOD_SUMMARY',
  VOD_TITLE = 'VOD_TITLE',
}

export abstract class BaseWidget {
  type!: ProjectCMSWidgetType
  name!: string
  width!: number
  height!: number
  x!: number
  y!: number
  padding?: number
  backgroundColor?: string
}

export class DynamicTextWidget extends BaseWidget {
  key?: string
  color?: string
  fontFamily?: string
  fontSize?: number
  alignment?: string
  fontWeight?: string
}

export class DynamicImageWidget extends BaseWidget {
  key?: string
  objectFit?: string
  alignment?: string
}

export class ClockWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.CLOCK
}

export class ClockDigitalWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.CLOCK_DIGITAL
}

export class CurrentDateWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.CURRENT_DATE
}

export class BrandLogoWidget extends DynamicImageWidget {
  override readonly type = ProjectCMSWidgetType.BRAND_LOGO
  mediaId?: string
}

export class AppNameWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.APP_NAME
}

export class AppDescriptionWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.APP_DESCRIPTION
}

export class AppLogoWidget extends DynamicImageWidget {
  override readonly type = ProjectCMSWidgetType.APP_LOGO
}

export class AppGridWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.APP_GRID
  nbColumns?: number
  nbLines?: number
  gap?: number
  itemBackgroundColor?: string
  focusBorderSize?: number
  focusBorderColor?: string
  focusBackgroundColor?: string
}

export class ChannelBannerContainerWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.CHANNEL_BANNER_CONTAINER
}

export class ChannelBannerTextWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.CHANNEL_BANNER_TEXT
  text?: string
}

export class ChannelTVWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.CHANNEL_TV
}

export class ChannelLogoWidget extends DynamicImageWidget {
  override readonly type = ProjectCMSWidgetType.CHANNEL_LOGO
}

export class ChannelNumberWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.CHANNEL_NUMBER
}

export class ChannelNameWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.CHANNEL_NAME
}

export class ChannelGridWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.CHANNEL_GRID
  nbColumns?: number
  nbLines?: number
  gap?: number
  itemBackgroundColor?: string
  focusBorderSize?: number
  focusBorderColor?: string
  focusBackgroundColor?: string
}

export class LanguageWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.LANGUAGE
}

export class MarriottAudioWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.MARRIOTT_AUDIO
}

export class MarriottSubtitleWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.MARRIOTT_SUBTITLE
}

export class ProgramTitleWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.PROGRAM_TITLE
}

export class ProgramDescriptionWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.PROGRAM_DESCRIPTION
}

export class ProgramTimeslotWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.PROGRAM_TIMESLOT
}

export class ProgramNextWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.PROGRAM_NEXT
}

export class IPGWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.IPG
  itemColor?: string
  itemBackgroundColor?: string
  focusColor?: string
  focusBackgroundColor?: string
  headerColor?: string
  headerBackgroundColor?: string
  showChannelIcons?: boolean
  showSmartApps?: boolean
  elementFontSize?: number
  headerFontSize?: number
}

export class WeatherWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.WEATHER
  city?: string
  mainColor?: string
  secondaryColor?: string
  tertiaryColor?: string
  textColor?: string
  cornerRadius?: number
}

export class MarriottWakeUpAlarmWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.MARRIOTT_WAKE_UP_ALARM
  textColor?: string
  buttonColor?: string
}

export class MarriottAlarmRingingWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.MARRIOTT_ALARM_RINGING
  textColor?: string
  buttonColor?: string
}

export class MarriottPmsMessagesWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.MARRIOTT_PMS_MESSAGES
  textColor?: string
  buttonColor?: string
}

export class MarriottPmsMessagesIndicatorWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.MARRIOTT_PMS_MESSAGES_INDICATOR
  chipColor?: string
  textColor?: string
}

export class MarriottViewBillWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.MARRIOTT_VIEW_BILL
  textColor?: string
  buttonColor?: string
}

export class VODGridWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.VOD_GRID
}

export class VODPlayerWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.VOD_PLAYER
}

export class VODSummaryWidget extends BaseWidget {
  override readonly type = ProjectCMSWidgetType.VOD_SUMMARY
}

export class VODTitleWidget extends DynamicTextWidget {
  override readonly type = ProjectCMSWidgetType.VOD_TITLE
}
